/**
 * NAT-2481 — the client aging table as XLSX: the clients the bucket filter leaves, in the order
 * on screen, and their invoices on a second sheet. A position as of a day: these figures have no
 * period. No request is made — the table already holds every client the endpoint returned.
 */
import type { ForwardBand, IAgingBucketData, IAgingClient, IAgingInvoice } from 'src/types';
import { toFiniteNumber } from 'src/utils/strict-number';
import { ymdToDate, type XlsxWorkbookSpec } from 'src/utils/file-downloader';

import { AGING_BUCKET_CONFIG } from '../aging-bucket-config';
import { FORWARD_BANDS, forwardBandLabelKey } from './forward-bands';
import { agingPreamble, anyCarries, sheetOf, type ExportT } from '../position-export';
import {
  anyPaymentTime,
  invoiceNoteKey,
  openAmountOf,
  orderCodesOf,
  priorityOnFloor,
  termsLabel,
} from './client-aging-labels';

export function clientAgingWorkbook({
  clients,
  page,
  asOf,
  bucketLabel,
  agentLabel,
  includeAgent = false,
  includeCollection = true,
  includePaymentTime,
  forwardBands,
  sortLabel,
  t,
  now = new Date(),
}: {
  /** Already filtered and sorted as on screen. */
  clients: IAgingClient[];
  /** The page (and section) the table sits on, in the viewer's language. */
  page: string;
  /** The response's own as-of day, when it states one. */
  asOf?: string | null;
  bucketLabel: string | null;
  /** The admin's agent filter in words; the preamble carries a line only when there is one. */
  agentLabel?: string | null;
  /** The «Agente» column: an admin's. An agent's file has none. */
  includeAgent?: boolean;
  /** The collection workflow (status, last contact, notes): an admin's. An agent's file has none. */
  includeCollection?: boolean;
  /**
   * The payment columns. The table decides from every client it holds, so the file agrees with the
   * screen whatever the bucket filter leaves; absent, the rows handed in decide.
   */
  includePaymentTime?: boolean;
  /**
   * NAT-2801 — the response's «In corso» bands. Absent (a backend that predates them): no
   * «Scadenze future» sheet, never one of zeros.
   */
  forwardBands?: Record<ForwardBand, IAgingBucketData> | null;
  sortLabel: string;
  t: ExportT;
  now?: Date;
}): XlsxWorkbookSpec {
  const carriesPaymentTime = includePaymentTime ?? anyPaymentTime(clients);
  const clientNote = (c: IAgingClient) =>
    [
      priorityOnFloor(c) ? t('export_priorita_minima') : null,
      includeCollection && c.lastContactedAt === null ? t('mai_contattato') : null,
    ]
      .filter(Boolean)
      .join('; ') || null;

  const invoices = clients.flatMap((client) =>
    (client.invoices ?? []).map((invoice) => ({ client, invoice })),
  );
  const invoiceNote = ({ invoice }: { invoice: IAgingInvoice }) => {
    const key = invoiceNoteKey(invoice);
    return key ? t(key) : null;
  };

  // «Scaduta» for an overdue invoice (its band is null); empty for a row with no band to name.
  const bandOf = ({ invoice }: { invoice: IAgingInvoice }) => {
    if (invoice.forwardBand) {
      const key = forwardBandLabelKey(invoice.forwardBand);
      return key ? t(key) : null;
    }
    return invoice.daysOverdue > 0 ? t('scaduta') : null;
  };

  const allBandRows = forwardBands
    ? FORWARD_BANDS.map(({ key, labelKey }) => {
        const amount = toFiniteNumber(forwardBands[key]?.amount);
        const count = toFiniteNumber(forwardBands[key]?.count);
        return {
          label: t(labelKey),
          amount,
          // The tiles show a dash for an unusable amount and hide a negative count; the cells
          // stay empty in exactly those cases, never a count beside a missing figure.
          count: amount === null || count === null || count < 0 ? null : count,
        };
      })
    : null;
  // As the tiles: a response with no usable amount in any band has nothing to summarise.
  const bandRows = allBandRows?.some((r) => r.amount !== null) ? allBandRows : null;

  return {
    fileName: `${page} - ${t('export_clienti')}`,
    preamble: [
      ...agingPreamble({ page, asOf, bucketLabel, agentLabel, sortLabel, t, now }),
      // The summary sheet shares the workbook's preamble, so say what it is not narrowed by.
      ...(bandRows ? [[t('export_scadenze'), t('export_scadenze_nota')] as [string, string]] : []),
    ],
    sheets: [
      sheetOf(
        t('export_clienti'),
        [
          {
            header: t('priorita'),
            kind: 'decimal',
            cell: (c) => c.priorityScore,
            carried: anyCarries(clients, (c) => c.priorityScore),
          },
          { header: t('cliente'), kind: 'text', cell: (c) => c.clientName },
          {
            header: t('agente'),
            kind: 'text',
            cell: (c) => c.agentName,
            carried: includeAgent && anyCarries(clients, (c) => c.agentName),
          },
          {
            header: t('export_ultima_nota'),
            kind: 'text',
            cell: (c) => c.latestNote,
            carried: includeCollection && anyCarries(clients, (c) => c.latestNote),
          },
          { header: t('totale_scaduto'), kind: 'euro', cell: (c) => c.totalOverdue },
          {
            header: t('bucket_peggiore'),
            kind: 'text',
            cell: (c) => t(AGING_BUCKET_CONFIG[c.worstBucket].labelKey),
          },
          {
            header: t('stato_incasso'),
            kind: 'text',
            // As the chip: a null status and the stored «none» read the same.
            cell: (c) => t(`stato_${c.collectionStatus ?? 'none'}`),
            carried: includeCollection && anyCarries(clients, (c) => c.collectionStatus),
          },
          {
            header: t('ultimo_contatto'),
            kind: 'date',
            cell: (c) => ymdToDate(c.lastContactedAt),
            carried: includeCollection && anyCarries(clients, (c) => c.lastContactedAt),
          },
          { header: t('n_fatture'), kind: 'integer', cell: (c) => c.invoiceCount },
          // NAT-2800 — days and count as numbers (a file is for sorting and summing); the terms
          // read as on screen, since «concordati» / «in fattura» is part of the answer.
          {
            header: t('export_pagamento_medio'),
            kind: 'integer',
            cell: (c) => c.paymentTime?.days ?? null,
            carried: carriesPaymentTime,
          },
          {
            header: t('export_n_pagamenti'),
            kind: 'integer',
            cell: (c) => (c.paymentTime?.days == null ? null : c.paymentTime.payments),
            carried: carriesPaymentTime,
          },
          {
            header: t('termini'),
            kind: 'text',
            cell: (c) => (c.paymentTime ? termsLabel(c, t) : null),
            carried: carriesPaymentTime,
          },
          {
            header: t('export_col_note'),
            kind: 'text',
            cell: clientNote,
            carried: clients.some((c) => clientNote(c) !== null),
          },
        ],
        clients,
      ),
      sheetOf(
        t('export_fatture'),
        [
          { header: t('cliente'), kind: 'text', cell: ({ client }) => client.clientName },
          {
            header: t('codice_ordine'),
            kind: 'text',
            cell: ({ invoice }) => orderCodesOf(invoice).join(', '),
          },
          {
            header: t('numero_fattura'),
            kind: 'text',
            cell: ({ invoice }) => invoice.invoiceNumber,
            carried: anyCarries(invoices, ({ invoice }) => invoice.invoiceNumber),
          },
          { header: t('importo'), kind: 'euro', cell: ({ invoice }) => openAmountOf(invoice) },
          {
            header: t('data_scadenza'),
            kind: 'date',
            cell: ({ invoice }) => ymdToDate(invoice.invoiceDueDate),
          },
          {
            header: t('giorni_ritardo'),
            kind: 'integer',
            cell: ({ invoice }) => invoice.daysOverdue,
          },
          {
            header: t('export_col_note'),
            kind: 'text',
            cell: invoiceNote,
            carried: invoices.some((row) => invoiceNote(row) !== null),
          },
          {
            header: t('export_fascia_scadenza'),
            kind: 'text',
            cell: bandOf,
            carried: anyCarries(invoices, ({ invoice }) => invoice.forwardBand),
          },
        ],
        invoices,
      ),
      ...(bandRows
        ? [
            sheetOf(
              t('export_scadenze'),
              [
                { header: t('export_fascia_scadenza'), kind: 'text', cell: (r) => r.label },
                { header: t('importo'), kind: 'euro', cell: (r) => r.amount },
                { header: t('n_fatture'), kind: 'integer', cell: (r) => r.count },
              ],
              bandRows,
            ),
          ]
        : []),
    ],
  };
}
